import { Injectable } from "@angular/core";
import { collection, doc, DocumentData, Firestore, getDoc, onSnapshot, orderBy, query } from "@angular/fire/firestore";
import { AuthService } from "@core/auth.service";
import { AuthStateService } from "@core/auth-state.service";
import { BehaviorSubject, combineLatest, from, Observable, of } from "rxjs";
import { filter, map, shareReplay, switchMap, take } from "rxjs/operators";
import { CommonService } from "src/app/services/common/common.service";
import { RequestPaymentService } from "src/app/services/RequestBodyService/RequestPayment/request-payment.service";
import { TranslateService } from "@ngx-translate/core";
import { environment } from "src/environments/environment";

@Injectable({
  providedIn: "root",
})
export class CashOutRepositoryService {
  private cashOutStatus$ = new BehaviorSubject<any[]>([]);

  cashOutLatestStatus$ = this.getCashOutStatus().pipe(
    map((cashOutData) => {
      var cashOut = Object.assign(cashOutData);
      if (cashOut.length <= 0) return;
      return cashOut.sort((a, b) => a.withdrawal_date.toDate() - b.withdrawal_date.toDate())[cashOut.length - 1];
    })
  );

  constructor(
    private firestore: Firestore,
    private authService: AuthService,
    private authStateService: AuthStateService,
    private RequestPaymentService: RequestPaymentService,
    private commonService: CommonService,
    private translate: TranslateService
  ) { }

  getCashOut(): Observable<any> {
    const cashOutRulesDocRef = doc(this.firestore, "/cash_out_rules/XQkGB7bDbNVSOaDBvdGV");
    const systemReferenceDocRef = doc(this.firestore, `/general_modules/${environment.mirrorPath}system_reference`);

    // Fetch data from both documents using `getDoc()`
    const cashOutRules$ = from(getDoc(cashOutRulesDocRef)).pipe(
      map((docSnapshot) => docSnapshot.data())
    );

    const systemReference$ = from(getDoc(systemReferenceDocRef)).pipe(
      map((docSnapshot) => docSnapshot.data())
    );

    // Combine the observables and merge the data
    return combineLatest([cashOutRules$, systemReference$]).pipe(
      map((cashOut: any[]) => {
        return { ...cashOut[0], ...cashOut[1] }; // Merge both document data
      })
    );
  }

  getCashOutStatus(): Observable<any[]> {
    // ✅ FIX: Gate on customTokenReady$ before opening the onSnapshot listener
    // to prevent permission-denied race condition (LOTTOSOCIAL-PWA-1MED).
    this.authStateService.customTokenReady$.pipe(take(1)).subscribe(() => {
      this.commonService.getUid("CashOutRepositoryService").subscribe({
        next: (userId) => {
          if (!userId) {
            this.cashOutStatus$.next([]);
            return;
          }

          const cashoutStatusCollectionRef = collection(
            this.firestore,
            `users/${userId}/cashout_status`
          );
          const cashoutStatusQuery = query(
            cashoutStatusCollectionRef,
            orderBy("withdrawal_date", "desc")
          );
          onSnapshot(
            cashoutStatusQuery,
            (snapshot) => {
              const actions = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...(doc.data() as DocumentData),
              }));

              this.cashOutStatus$.next(this.snapShotData(actions));
            },
            (error) => {
              if ((error as { code?: string })?.code === "permission-denied") {
                this.cashOutStatus$.next([]);
                return;
              }
              this.cashOutStatus$.error(error);
            }
          );
        },
        error: (err) => {
          return err;
        },
      });
    });

    return this.cashOutStatus$.asObservable().pipe(shareReplay(1));
  }

  // updateCreditCashOut(cashOutValue) {
  //   // const api = this.angularFireFunctions.httpsCallable("api");
  //   // return api(this.requestBodyService.updateCreditCashOut(cashOutValue));
  //   return this.authService?.getV2CloudFunction("apiv2", this.RequestPaymentService.updateCreditCashOut(cashOutValue));
  // }

  updateCreditCashOut(cashOutValue: any): Observable<any> {
    return this.RequestPaymentService.updateCreditCashOut(cashOutValue).pipe(
      filter((req) => req !== null), // ignore invalid
      switchMap((req) =>
        this.authService.getV2CloudFunction("apiv2", req)
      )
    );
  }

  updateCharityCashOut(cashOutValue) {
    // const api = this.angularFireFunctions.httpsCallable("api");
    // return api(this.requestBodyService.updateCharityCashOut(cashOutValue));
    return this.RequestPaymentService.updateCharityCashOut(cashOutValue).pipe(switchMap((res) => {
      return this.authService.getV2CloudFunction("apiv2", res)
    }))
    // return this.authService?.getV2CloudFunction("apiv2", this.RequestPaymentService.updateCharityCashOut(cashOutValue));
  }

  updatePayPalCashOut(cashOutValue: any, payPalEmail: string): Observable<any> {
    return this.RequestPaymentService.updatePayPalCashOut(cashOutValue, payPalEmail).pipe(
      filter((req) => req !== null), // block invalid requests
      switchMap((req) =>
        this.authService.getV2CloudFunction("apiv2", req)
      )
    );
  }

  updatePayPalCashOutEmail(email: string): void {
    const userId = this.authService.getUid$("CashOutRepositoryService").pipe(
      switchMap((userId) => {
        return of(userId) || of(null);
      }));
    if (!userId) {
      console.error("User ID is not available.");
      return;
    }
    const path_id = "yzF1Ss5eiMqnh91zQMtv";
    const doc_data = { payPalEmail: email };
    const method = "update";

    this.authService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      method,
    }).subscribe({
      error: (error) => {
        console.error("Error updating PayPal email:", error);
        return error;
      }
    });
  }
  private snapShotData(actions: any[]): any[] {
    return actions.map((a) => {
      const id = a.id; // Extract the document ID
      const data = a;  // Extract the document data
      return { id, ...data }; // Combine ID and document data into one object
    });
  }

  getTranslatedStatus(text: string): string {
    if (!text || typeof text !== 'string') return '';
  
    const map: Record<string, string> = {
      'processed on': 'cashout-status.processed_on',
      'processing time may vary': 'cashout-status.processing_vary',
      'will be processed shortly': 'cashout-status.processed_shortly',
      'find out more': 'cashout-status.find_out_more'
    };
  
    const normalized = text.toLowerCase().replace(/\s+/g, ' ').trim();
  
    // Detect date anywhere in text
    const dateMatch = text.match(
      /(\d{1,2}[\/\-.]\d{1,2}(?:[\/\-.]\d{2,4})?|\d{4}[\/\-.]\d{1,2}[\/\-.]\d{1,2})/
    );
    const date = dateMatch ? dateMatch[1] : '';
  
    for (const phrase in map) {
      if (normalized.includes(phrase)) {
        // Translate the phrase
        const translated = this.translate.instant(map[phrase]);
        // Append date manually
        return date ? `${translated} ${date}` : translated;
      }
    }
  
    return text;
  }
}
